import { useState } from "react";
import { ChevronRight, RefreshCw } from "lucide-react";
import { useTenant } from "@/lib/tenant-context.js";
import { useAsync } from "@/hooks/use-async.js";
import { Skeleton } from "@/components/ui/skeleton.js";
import { Badge } from "@/components/ui/badge.js";
import { Button } from "@/components/ui/button.js";
import { useToast } from "@/components/ui/toast.js";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog.js";
import { ApiError } from "@/lib/api.js";
import type { WebhookDelivery } from "@/lib/types.js";

/**
 * Dead-letter queue viewer (Implementation Checklist). Lists webhook deliveries
 * in the `dead_letter` state and lets staff inspect the payload, attempts log,
 * last response excerpt, and trigger a manual replay.
 *
 * Falls back gracefully when the backend's analytics summary endpoint is the
 * only signal available (no /webhook-deliveries list yet).
 */
export function DlqPage(): React.JSX.Element {
  const { api } = useTenant();
  const { toast } = useToast();
  const dead = useAsync(() => api.listDeadLetterDeliveries(), [api]);
  const subs = useAsync(() => api.listWebhookSubscriptions(), [api]);
  // Fallback summary only — used when the dead-letter listing is unavailable.
  const summary = useAsync(
    () =>
      api.request<{ webhook_dlq_count?: number }>("/api/v1/analytics/summary").catch(() => ({ webhook_dlq_count: undefined })),
    [api]
  );
  const [selected, setSelected] = useState<WebhookDelivery | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const items = dead.data?.data ?? [];

  async function openDetail(id: string): Promise<void> {
    setDetailLoading(true);
    // Render the row immediately so the dialog opens; populate full detail when
    // the GET returns. The list response only carries summary fields — the
    // detail endpoint joins outbox_event payload + attempts log.
    const summaryRow = items.find((d) => d.id === id) ?? null;
    setSelected(summaryRow);
    try {
      const full = await api.getWebhookDelivery(id);
      setSelected(full);
    } catch (error) {
      const code = error instanceof ApiError ? error.code : "error";
      const msg = error instanceof Error ? error.message : String(error);
      toast({ title: code, description: msg, variant: "destructive" });
    } finally {
      setDetailLoading(false);
    }
  }

  if (dead.loading || subs.loading) return <Skeleton className="h-48 w-full" />;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-lg font-semibold">Dead-letter queue</h1>
        <Button variant="ghost" size="sm" onClick={dead.reload}>
          <RefreshCw className="mr-1 h-4 w-4" />
          Refresh
        </Button>
      </div>

      <div className="rounded-md border bg-card p-4 text-sm">
        {items.length > 0 || dead.error ? (
          <span>
            <strong>{items.length}</strong> dead deliveries
          </span>
        ) : (
          <span>
            <strong>{summary.data?.webhook_dlq_count ?? 0}</strong> dead deliveries (summary)
          </span>
        )}
      </div>

      {items.length === 0 ? (
        <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
          {dead.error
            ? `DLQ list unavailable (${dead.error.message}); using analytics summary.`
            : "No dead-letter deliveries. Healthy."}
        </div>
      ) : (
        <ul className="flex flex-col gap-2">
          {items.map((delivery) => (
            <li
              key={delivery.id}
              className="flex cursor-pointer items-center justify-between gap-2 rounded-md border bg-card p-3 text-sm shadow-sm hover:bg-accent"
              onClick={() => void openDetail(delivery.id)}
            >
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs text-muted-foreground">
                    {delivery.id.slice(0, 8)}
                  </span>
                  {delivery.outbox_event?.event_type && (
                    <span className="font-medium">{delivery.outbox_event.event_type}</span>
                  )}
                  <Badge variant="destructive">dead</Badge>
                  {delivery.last_status_code !== undefined && (
                    <Badge variant="outline">HTTP {delivery.last_status_code}</Badge>
                  )}
                </div>
                <div className="text-xs text-muted-foreground">
                  {delivery.attempt_count} attempts ·{" "}
                  {delivery.last_attempt_at ? new Date(delivery.last_attempt_at).toLocaleString() : "n/a"}
                </div>
              </div>
              <ChevronRight className="h-4 w-4 opacity-60" />
            </li>
          ))}
        </ul>
      )}

      <h2 className="mt-2 text-sm font-medium">Subscriptions</h2>
      <ul className="flex flex-col gap-1">
        {(subs.data?.data ?? []).map((s) => (
          <li key={s.id} className="flex items-center justify-between rounded border px-3 py-2 text-xs">
            <code>{s.url}</code>
            <Badge variant={s.state === "active" ? "success" : "destructive"}>{s.state}</Badge>
          </li>
        ))}
      </ul>

      <DeliveryDetailDialog
        delivery={selected}
        loading={detailLoading}
        onOpenChange={(v) => !v && setSelected(null)}
        onReplay={async (id) => {
          try {
            await api.replayWebhookDelivery(id);
            toast({ title: "Replay queued", variant: "success" });
            dead.reload();
            setSelected(null);
          } catch (error) {
            const code = error instanceof ApiError ? error.code : "error";
            const msg = error instanceof Error ? error.message : String(error);
            toast({ title: code, description: msg, variant: "destructive" });
          }
        }}
      />
    </div>
  );
}

function DeliveryDetailDialog({
  delivery,
  loading,
  onOpenChange,
  onReplay
}: {
  delivery: WebhookDelivery | null;
  loading: boolean;
  onOpenChange: (v: boolean) => void;
  onReplay: (id: string) => Promise<void> | void;
}): React.JSX.Element {
  const [busy, setBusy] = useState(false);
  return (
    <Dialog open={delivery !== null} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Delivery detail{loading ? " (loading…)" : ""}</DialogTitle>
          <DialogDescription>
            Inspect the payload, attempts, and last response. Replaying re-queues the
            delivery — the subscription receives the original event with the same
            idempotency key so duplicate writes are safe at the consumer.
          </DialogDescription>
        </DialogHeader>

        {delivery && (
          <div className="flex flex-col gap-3 text-xs">
            <Section title="Identity">
              <KV k="ID" v={delivery.id} />
              <KV k="Subscription" v={delivery.subscription_id} />
              <KV k="Event" v={delivery.outbox_event_id} />
              <KV k="State" v={delivery.state} />
              <KV k="Attempts" v={String(delivery.attempt_count)} />
              <KV k="Last status" v={delivery.last_status_code ? String(delivery.last_status_code) : "—"} />
              <KV
                k="Last attempt"
                v={delivery.last_attempt_at ? new Date(delivery.last_attempt_at).toLocaleString() : "—"}
              />
            </Section>

            {delivery.outbox_event && (
              <Section title="Payload">
                <pre className="max-h-48 overflow-auto rounded bg-muted/40 p-2 text-[11px]">
                  {JSON.stringify(delivery.outbox_event.payload, null, 2)}
                </pre>
              </Section>
            )}

            {delivery.last_response_excerpt && (
              <Section title="Last response">
                <pre className="max-h-32 overflow-auto rounded bg-muted/40 p-2 text-[11px]">
                  {delivery.last_response_excerpt}
                </pre>
              </Section>
            )}

            {delivery.attempts && delivery.attempts.length > 0 && (
              <Section title="Attempts">
                <ul className="flex flex-col gap-1">
                  {delivery.attempts.map((attempt, i) => (
                    <li key={i} className="grid grid-cols-[120px_1fr_auto] gap-2 rounded border px-2 py-1">
                      <span>{new Date(attempt.attempted_at).toLocaleString()}</span>
                      <span className="text-muted-foreground">{attempt.error ?? "OK"}</span>
                      <span>
                        {attempt.status_code ? `${attempt.status_code}` : "—"}
                        {attempt.duration_ms ? ` · ${attempt.duration_ms}ms` : ""}
                      </span>
                    </li>
                  ))}
                </ul>
              </Section>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          {delivery && (
            <Button
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  await onReplay(delivery.id);
                } finally {
                  setBusy(false);
                }
              }}
            >
              {busy ? "Replaying…" : "Replay"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <div>
      <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {title}
      </div>
      {children}
    </div>
  );
}

function KV({ k, v }: { k: string; v: string }): React.JSX.Element {
  return (
    <div className="grid grid-cols-[120px_1fr] gap-2 text-xs">
      <span className="text-muted-foreground">{k}</span>
      <span className="font-mono">{v}</span>
    </div>
  );
}
